import { ReactElement } from 'react';
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import ChartCard from '../ChartCard';

interface WindDirectionPlotProps {
  configs: any[];
  utcOffsetSeconds: number;
  weatherControllerDict: any;
  height?: number;
}

export default function WindDirectionPlot({
  configs,
  utcOffsetSeconds,
  weatherControllerDict,
  height = 300,
}: WindDirectionPlotProps): ReactElement {
  const probeDirectionData: any[] = [];

  // Collect wind direction data from all runs (probe)
  for (const config of configs) {
    for (const run of config.runs || []) {
      const avgWindDirection = run.average?.WindDirAbs_AG3?.[0];
      const runTime = run.average?.GatesStartTs_tG3?.[0];

      if (avgWindDirection == null || runTime == null) {
        continue;
      }

      probeDirectionData.push({
        x: (runTime + utcOffsetSeconds) * 1000, // Convert to milliseconds
        y: avgWindDirection,
      });
    }
  }

  // Collect Weather API wind direction data
  const apiDirectionData: any[] = [];
  const weatherApiSource = weatherControllerDict?.weather_api;
  if (weatherApiSource?.weather_data) {
    const validData = weatherApiSource.weather_data
      .filter((d: any) => d.WindDir_A !== null && d.TimeAbs_t !== null)
      .sort((a: any, b: any) => a.TimeAbs_t - b.TimeAbs_t);

    for (const d of validData) {
      apiDirectionData.push({
        x: (d.TimeAbs_t + utcOffsetSeconds) * 1000,
        y: d.WindDir_A,
      });
    }
  }

  if (probeDirectionData.length === 0 && apiDirectionData.length === 0) {
    return (
      <div className="cr-flex cr-items-center cr-justify-center cr-rounded-xl cr-bg-gray-50 cr-text-gray-500" style={{ height: `${height}px` }}>
        No wind direction data available
      </div>
    );
  }

  // Sort by time
  probeDirectionData.sort((a, b) => a.x - b.x);

  // Calculate time range: 90 minutes before first probe run and 90 minutes after last probe run
  const minTime =
    probeDirectionData.length > 0
      ? probeDirectionData[0].x
      : apiDirectionData.length > 0
        ? apiDirectionData[0].x
        : Date.now();
  const maxTime =
    probeDirectionData.length > 0
      ? probeDirectionData[probeDirectionData.length - 1].x
      : apiDirectionData.length > 0
        ? apiDirectionData[apiDirectionData.length - 1].x
        : Date.now();
  const ninetyMinutesMs = 90 * 60 * 1000;

  const seriesData: any[] = [];
  const colorArray: string[] = [];

  if (probeDirectionData.length > 0) {
    seriesData.push({
      name: 'Probe Wind Direction',
      data: probeDirectionData,
    });
    colorArray.push('#10b981'); // Green
  }

  if (apiDirectionData.length > 0) {
    seriesData.push({
      name: 'Weather API Wind Direction',
      data: apiDirectionData,
    });
    colorArray.push('#3b82f6'); // Blue
  }

  const options: ApexOptions = {
    chart: {
      type: 'scatter',
      height: height,
      zoom: { enabled: true, allowMouseWheelZoom: false },
      toolbar: { show: true },
      animations: { enabled: false },
    },
    markers: {
      size: 6,
      strokeColors: '#fff',
      strokeWidth: 2,
    },
    colors: colorArray,
    xaxis: {
      type: 'datetime',
      min: minTime - ninetyMinutesMs,
      max: maxTime + ninetyMinutesMs,
      labels: {
        datetimeUTC: true,
        format: 'HH:mm',
      },
      title: {
        text: 'Time of Day',
        style: {
          fontSize: '12px',
          fontWeight: 600,
          color: '#374151',
        },
      },
    },
    yaxis: {
      min: 0,
      max: 360,
      tickAmount: 8,
      title: {
        text: 'Wind Direction (degrees)',
        style: {
          fontSize: '12px',
          fontWeight: 600,
          color: '#374151',
        },
      },
      labels: {
        formatter: (val: number) => val.toFixed(0),
      },
    },
    grid: {
      borderColor: '#e5e7eb',
    },
    legend: {
      show: true,
      position: 'top',
      horizontalAlign: 'left',
      fontSize: '12px',
      fontWeight: 500,
    },
    tooltip: {
      x: {
        format: 'HH:mm:ss',
      },
      y: {
        formatter: (val: number) => `${val.toFixed(1)}°`,
      },
    },
    title: {
      text: 'Wind Direction',
      align: 'center',
    },
  };

  return (
    <ChartCard height={`${height}px`} padding>
      <Chart options={options} series={seriesData} type="scatter" height="100%" />
    </ChartCard>
  );
}
